import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@elbklar/ui';
import { tasksApi } from '@/lib/api';
import { formatApiError } from '@/lib/validation';
import { TaskSheet } from './TaskSheet';

interface AufgabeBearbeitenProps {
  /** Aufgabe, die bearbeitet wird; `null` heisst zu. */
  aufgabeId: string | null;
  /** Muss stabil sein (`useCallback`), sonst meldet derselbe Fehler mehrfach. */
  onClose: () => void;
}

/**
 * Eine Aufgabe aus der Übersicht bearbeiten: derselbe Dialog wie auf der
 * Aufgabenseite, die Aufgabe selbst aber frisch über `GET /tasks/{id}`.
 *
 * Warum nachgeladen wird: Die Übersicht bekommt ihre Aufgaben aus
 * `GET /dashboard` in Kurzform — ohne Beschreibung. Diese Kurzform ins
 * Formular zu geben hiesse, beim Speichern eine vorhandene Beschreibung
 * stillschweigend zu leeren. Auf der Kunden- und der Partnerseite liegt die
 * ganze Aufgabe vor (`GET /tasks/entity/…`), dort geht sie direkt ins Sheet.
 *
 * Der Dialog öffnet deshalb erst, wenn die Aufgabe da ist; react-query behält
 * sie danach, ein zweites Öffnen ist sofort da. Scheitert das Laden, sagt es
 * ein Toast — ein Stift, der nichts tut, wäre ein Fehler ohne Spur.
 */
export function AufgabeBearbeiten({ aufgabeId, onClose }: AufgabeBearbeitenProps) {
  const { data: aufgabe, error } = useQuery({
    queryKey: ['tasks', 'einzeln', aufgabeId],
    queryFn: () => tasksApi.get(aufgabeId!),
    enabled: !!aufgabeId,
  });

  useEffect(() => {
    if (!error) return;
    toast.error(formatApiError(error) || 'Aufgabe liess sich nicht laden');
    onClose();
  }, [error, onClose]);

  if (!aufgabeId || !aufgabe) return null;

  return (
    <TaskSheet
      open
      onOpenChange={(offen) => {
        if (!offen) onClose();
      }}
      task={aufgabe}
    />
  );
}
